import React, { useLayoutEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import { isNullish, isEmptyOrBlank, agentTimezoneMapping } from "utils/utils";
import { queryAgentDetail, updateStatus } from "./model";
import { emailFormatValidator, isAllInputValid } from "utils/formValidation";
import s from "./AgentsDetail.module.scss";

import Spin from "components/Spin/Spin";
import Selection from "components/Input/Selection/Selection";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import RoleIdSearchField from "components/RoleIdSearchField/RoleIdSearchField";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import responseCodeMapping from "utils/response_code_mapping.json";

import EnabledOptions from "utils/commonOptions/EnabledOptions";
import { getRoleOptions } from "../agentUtils";
import { Link, Typography } from "@mui/material";
import useColors from "utils/useColors/useColors";
import useConfig from "utils/useConfig/useConfig";

const PAGE_LOCALE_KEY = "page.agents.agents_detail.";

const AGENTDETAIL_FIELD_INIT_STATE = {
  firstName: "",
  lastName: "",
  email: "",
  role: "",
  isOTPenable: "",
  timezone: "",
  permission: [],
  status: "",
  roleId: "",
};

const ERROR_INIT_STATE = {
  firstName: false,
  lastName: false,
  email: false,
  role: false,
  isOTPenable: false,
  timezone: false,
  permission: false,
  status: false,
  roleId: false,
};

const AgentsDetailInfo = ({ isoAgentId, updateCallback }) => {
  const { t } = useTranslation();
  const c = useColors();
  const { config } = useConfig();
  const { user, logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "AgentMerchantsListGrid";

  const [detailLoading, setDetailLoading] = useState(true);

  const [agentDetailFieldInput, setAgentDetailFieldInput] = useState(
    AGENTDETAIL_FIELD_INIT_STATE,
  );
  const [focus, setFocus] = useState({});
  const [error, setError] = useState(ERROR_INIT_STATE);

  const [submitting, setSubmitting] = useState(false);

  const [dataValid, setDataValid] = useState(false);

  useLayoutEffect(() => {
    if (!isoAgentId) {
      //message "unknown payment id" will be shown
      setDetailLoading(false);
      return;
    }
    initialFetchDetails();
  }, [isoAgentId]);

  const initialFetchDetails = async (getIsOudated) => {
    setDetailLoading(true);
    const apiResponse = await queryAgentDetail(isoAgentId);
    if (getIsOudated && getIsOudated()) return;
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            const resultData = responseBody.data;
            if (resultData) {
              setAgentDetailFieldInput((state) => ({
                ...state,
                isoAgentId: isoAgentId,
                firstName: resultData.firstName,
                lastName: resultData.lastName,
                email: resultData.email,
                role: resultData.role,
                isOTPenable: resultData.isOTPenable,
                timezone: agentTimezoneMapping(resultData.timezone), // MAS-349. This function is aligned OBS & OP timezones behaviour when creating and rendering agent detail.
                permission:
                  JSON.parse(resultData.permission ?? "{}")?.allowedComponent ??
                  [],
                status: resultData.status,
                roleId: `${resultData.roleId ?? ""}`,
              }));
              setDataValid(true);
            } else {
              snackbar(
                t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
                "error",
              );
            }
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
          }
        }
      }
    }

    setDetailLoading(false);
  };

  // ===================== Detail field function start ===================== //

  const textInputOnChange = (e, field, callback) => {
    setError({ ...error, [field]: false });
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setAgentDetailFieldInput({ ...agentDetailFieldInput, [field]: value });
  };

  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const textInputOnBlur = (field, callback) => {
    if (callback) {
      if (agentDetailFieldInput[field]) {
        const isValidValue = callback(agentDetailFieldInput[field]);
        if (!isValidValue) {
          setError({ ...error, [field]: error });
        }
      } else {
        const isValidValue = callback("");
        if (!isValidValue) {
          setError({ ...error, [field]: error });
        }
      }
    }
    setFocus({ [field]: false });
  };

  const isNotEmpty = (txt) => {
    if (isNullish(txt)) return false;
    if (txt == "") return false;
    return true;
  };

  // ===================== detail field function end ===================== //
  // ===================== detail function  ===================== //

  const roleOptions = getRoleOptions(user?.role);

  const canAssignRole = ["SUPER_ADMIN", "PF_ADMIN"].includes(user?.role);

  const sendDetailUpdate = async () => {
    const requiredField = ["firstName", "lastName", "email"];
    if (canAssignRole) {
      requiredField.push("roleId");
    }
    let hasError = false;
    requiredField.forEach((field) => {
      const fieldValue = agentDetailFieldInput[field];
      if (isEmptyOrBlank(fieldValue)) {
        console.log("error Field: ", field, "fieldValue", fieldValue);
        setError((error) => ({ ...error, [field]: true }));
        hasError = true;
      }
    });

    const allValid = isAllInputValid(
      error.firstName,
      error.lastName,
      error.email,
      error.role,
      error.isOTPenable,
      error.timezone,
      error.permission,
      canAssignRole ? error.roleId : false,
      hasError,
    );

    if (!allValid) {
      return;
    }
    setSubmitting(true);
    const payload = { ...agentDetailFieldInput };
    payload.isOTPenable = agentDetailFieldInput.isOTPenable != "0"; // cast isOTPenable to boolean
    payload.timezone = agentTimezoneMapping(payload.timezone); // MAS-349. This function is aligned OBS & OP timezones behaviour when creating and rendering agent detail.
    payload.permission = JSON.stringify({
      allowedComponent: agentDetailFieldInput.permission,
    });
    const apiResponse = await updateStatus(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode !== "0000") {
            const errorMessage =
              responseCodeMapping[responseBody.responseCode]?.["en"] ||
              responseBody.message ||
              "Unknown error";
            snackbar(
              t(PAGE_LOCALE_KEY + "update.msgFailed") + errorMessage,
              "error",
            );
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "update.msgSuccess"), "success");
            updateCallback();
          }
        }
      }
    }
    setSubmitting(false);
  };

  const AgentDetailsMainContent = () => {
    return (
      <div className={s.detailItemContainer}>
        <div className={s.content2}>
          <Grid gridId={gridId}>
            <OutlinedText
              mandatory={true}
              label={t(PAGE_LOCALE_KEY + "detailColumns.firstName.label")}
              value={agentDetailFieldInput.firstName}
              onchangeFn={(e) => textInputOnChange(e, "firstName")}
              onfocusFn={() => textInputOnFocus("firstName")}
              onblurFn={() => textInputOnBlur("firstName", isNotEmpty)}
              sxParams={{
                textFocus: focus.firstName,
                textError: error.firstName,
              }}
              errorMessage={t(
                PAGE_LOCALE_KEY + "detailColumns.firstName.errorInput",
              )}
            />
            <OutlinedText
              mandatory={true}
              label={t(PAGE_LOCALE_KEY + "detailColumns.lastName.label")}
              value={agentDetailFieldInput.lastName}
              onchangeFn={(e) => textInputOnChange(e, "lastName")}
              onfocusFn={() => textInputOnFocus("lastName")}
              onblurFn={() => textInputOnBlur("lastName", isNotEmpty)}
              sxParams={{
                textFocus: focus.lastName,
                textError: error.lastName,
              }}
              errorMessage={t(
                PAGE_LOCALE_KEY + "detailColumns.lastName.errorInput",
              )}
            />

            <OutlinedText
              mandatory={true}
              label={t(PAGE_LOCALE_KEY + "detailColumns.email.label")}
              value={agentDetailFieldInput.email}
              onchangeFn={(e) => textInputOnChange(e, "email")}
              onfocusFn={() => textInputOnFocus("email")}
              onblurFn={() => textInputOnBlur("email", emailFormatValidator)}
              sxParams={{ textFocus: focus.email, textError: error.email }}
              errorMessage={t(
                PAGE_LOCALE_KEY + "detailColumns.email.errorInput2",
              )}
            />
            <Selection
              mandatory={true}
              label={t(PAGE_LOCALE_KEY + "detailColumns.role.label")}
              selectionValue={agentDetailFieldInput.role}
              onchangeFn={(e) => textInputOnChange(e, "role")}
              onfocusFn={() => textInputOnFocus("role")}
              onblurFn={() => textInputOnBlur("role")}
              options={roleOptions}
              sxParams={{ textFocus: focus.role, textError: error.role }}
              errorMessage={t(
                PAGE_LOCALE_KEY + "detailColumns.role.errorInput",
              )}
            />

            <Selection
              mandatory={true}
              label={t(PAGE_LOCALE_KEY + "detailColumns.isOTPenable.label")}
              selectionValue={agentDetailFieldInput.isOTPenable?.toString()}
              onchangeFn={(e) => textInputOnChange(e, "isOTPenable")}
              onfocusFn={() => textInputOnFocus("isOTPenable")}
              onblurFn={() => textInputOnBlur("isOTPenable")}
              options={EnabledOptions("all")}
              sxParams={{
                textFocus: focus.isOTPenable,
                textError: error.isOTPenable,
              }}
              errorMessage={t(
                PAGE_LOCALE_KEY + "detailColumns.isOTPenable.errorInput",
              )}
            />
            {canAssignRole && (
              <RoleIdSearchField
                label={t(PAGE_LOCALE_KEY + "detailColumns.rolePermission.label")}
                value={agentDetailFieldInput.roleId}
                onChangeValue={(value) => {
                  setError((state) => ({ ...state, roleId: false }));
                  setAgentDetailFieldInput((state) => ({
                    ...state,
                    roleId: value,
                  }));
                }}
                currentUserRole={user?.role}
                required
                error={!!error.roleId}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.rolePermission.errorInput",
                )}
              />
            )}
          </Grid>
        </div>

        <div className={s.dialogActionContainer}>
          <Typography variant="content">
            <Trans
              i18nKey={PAGE_LOCALE_KEY + "disclaimer"}
              components={{
                ToU: <Link
                  color={c["brand1"]}
                  underline="none"
                  target="_blank"
                  href={config.TERMS_OF_USE}
                >
                  {t("layout.footer.terms")}
                </Link>,
                PP: <Link
                  color={c["brand1"]}
                  underline="none"
                  target="_blank"
                  href={config.PRIVACY_POLICY}
                >
                  {t("layout.footer.privacy")}
                </Link>
              }}
            />
          </Typography>
          <div className={s.dialogButtonContainer}>
            <ButtonMAS overlayLoading
              disabled={detailLoading}
              loading={submitting}
              onClick={sendDetailUpdate}
            >
              {t(PAGE_LOCALE_KEY + "btnUpdate")}
            </ButtonMAS>
          </div>
        </div>
      </div>
    );
  };

  // ===================== detail function end ===================== //

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  return (
    <div className={s.container}>
      {!detailLoading && !dataValid && (
        <div className={s.contentContainer}>
          <span>{t(PAGE_LOCALE_KEY + "fetchFail")}</span>
        </div>
      )}
      <div className={s.contentContainer}>
        {detailLoading ? <Spin /> : <></>}
        {submitting && <Spin type={"search"} />}
        <div
          className={[
            s.detailsContainer,
            detailLoading ? s.loadingNow : "",
            !dataValid ? s.inactiveTab : "",
          ].join(" ")}
        >
          <div className={s.agentDetails}>{AgentDetailsMainContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AgentsDetailInfo);
